import { useDeleteReferenceV2ApplyMutation } from '@core/app/api/applyJobV2Api'
import type { ApplyV2TempEducation } from '@core/app/types/applyV2Type'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2Card from '@core/components/ApplyDialogV2/ApplyV2Card'
import ApplyV2NoFieldsSwitch from '@core/components/ApplyDialogV2/ApplyV2NoFieldsSwitch'
import ApplyV2ReferenceStep2Add from '@core/components/ApplyDialogV2/ApplyV2ReferenceStep/ApplyV2ReferenceStep2/ApplyV2ReferenceStep2Add'
import ApplyV2ReferenceStep2Edit from '@core/components/ApplyDialogV2/ApplyV2ReferenceStep/ApplyV2ReferenceStep2/ApplyV2ReferenceStep2Edit'
import { ThisIcon } from '@core/components/ApplyDialogV2/ApplyV2ReferenceStep/icons/icon'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import React from 'react'
import './ApplyV2ReferenceStep2.scss'

const baseClass = 'ApplyV2ReferenceStep2'

function ApplyV2ReferenceStep2() {
    const {
        postProfileData,
        postProfileError,
        isPostProfileLoading,
        isPostProfileSuccess,
        isSettingsLoading,
        isSettingsFetching,
        isFinalStep,
        setIsFinalStep,
        isPostApplied,
        onCloseApply,
        settingsData,
        navItems,
        onNextStep,
        postProfileRefetch,
    } = useApplyV2Context()
    const { getTitle } = useApplyV2NavTranslations()
    const [itemLoading, setItemLoading] = React.useState<string | number | null>(null)
    const [editState, setEditState] = React.useState<'add' | string | null>(null)
    const [isNoItems, setIsNoItems] = React.useState(false)
    const [deleteReference, { isLoading: isDeleteLoading }] = useDeleteReferenceV2ApplyMutation()

    const items = postProfileData?.data?.reference ?? []

    const handleDeleteRequest = async (uid: string) => {
        try {
            setItemLoading(uid)
            await deleteReference({ uid: postProfileData?.data?.uid || '', referenceUid: uid }).unwrap()
            await postProfileRefetch()
        } catch (error) {
            console.log('Error deleting education:', error)
        } finally {
            setItemLoading(null)
        }
    }

    const getIsLoading = (uid?: string | number | null) => {
        return itemLoading === uid
    }

    const updated = items.find((c) => c.uid === editState || c.existingId === editState)

    if (editState === 'add') {
        return <ApplyV2ReferenceStep2Add onClose={() => setEditState(null)} />
    }

    if (editState && editState.length > 3 && editState !== 'add' && updated) {
        return <ApplyV2ReferenceStep2Edit onClose={() => setEditState(null)} item={updated} />
    }

    return (
        <div className={baseClass}>
            <div className={`${baseClass}__container`}>
                {items.length === 0 ? (
                    <div className={`${baseClass}__items ${baseClass}__empty`}>
                        <div className={` ${baseClass}__empty--icon`}>
                            <ThisIcon />
                        </div>
                        <div className={` ${baseClass}__empty--desc`}>
                            <div className={` ${baseClass}__empty--desc-title`}>
                                {isNoItems
                                    ? getTitle('10305', "I don't have any reference")
                                    : getTitle('10295', 'No references added yet')}
                            </div>
                            <div className={` ${baseClass}__empty--desc-btns`}>
                                <ApplyV2Button
                                    variant={'outline'}
                                    size={'small'}
                                    className={` ${baseClass}__empty--desc-btns--add`}
                                    onClick={() => {
                                        setEditState('add')
                                    }}
                                    isDisabled={isNoItems}
                                    leftIcon={<i className="fa-regular fa-plus"></i>}
                                >
                                    {getTitle('1211', 'Add reference')}
                                </ApplyV2Button>
                                <ApplyV2NoFieldsSwitch
                                    className={`${baseClass}__empty--desc-switch`}
                                    checked={isNoItems}
                                    onChange={(v) => setIsNoItems(v)}
                                    label={getTitle('6178', 'No references')}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={`${baseClass}__items`}>
                        {items.map((item) => (
                            <ApplySkeleton
                                isLoading={getIsLoading(item.uid || item.existingId)}
                                key={item.uid ?? item.existingId}
                                style={{
                                    height: '150px',
                                    marginBottom: '1rem',
                                    display: 'block',
                                    width: '100%',
                                }}
                            >
                                <ApplyV2Card
                                    // key={item.uid ?? item.existingId}
                                    className={`${baseClass}__items--course`}
                                    title={decodeHtmlEntitiesRecursive(item.name)}
                                    emailSection={item.email}
                                    headerDescription={decodeHtmlEntitiesRecursive(
                                        [item.companyName, item.title].filter(Boolean).join(' • ')
                                    )}
                                    phone={{ prefix: item.mobilePhone?.prefix, number: item.mobilePhone?.number }}
                                    // localeSection={item.location}
                                    onDelete={() => {
                                        handleDeleteRequest(item.uid)
                                    }}
                                    onEdit={() => setEditState(item.uid)}
                                    // date={{
                                    //     startDate: item.fromDate,
                                    //     endDate: item.toDate,
                                    //     dateFormat: 'MMM YYYY',
                                    //     showMonths: true,
                                    // }}
                                >
                                    {item.description}
                                </ApplyV2Card>
                            </ApplySkeleton>
                        ))}
                        {isPostProfileLoading && !itemLoading && (
                            <ApplySkeleton
                                isLoading={isPostProfileLoading}
                                style={{
                                    height: '150px',
                                    marginBottom: '1rem',
                                    display: 'block',
                                    width: '100%',
                                }}
                            />
                        )}
                        <ApplyV2Button
                            variant={'transparent'}
                            className={`${baseClass}__addBtn`}
                            onClick={() => {
                                setEditState('add')
                            }}
                            leftIcon={
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M7.99967 1.33301C8.36786 1.33301 8.66634 1.63148 8.66634 1.99967V7.12611H13.9997C14.3679 7.12611 14.6663 7.42459 14.6663 7.79278C14.6663 8.16097 14.3679 8.45944 13.9997 8.45944H8.66634V13.9997C8.66634 14.3679 8.36786 14.6663 7.99967 14.6663C7.63148 14.6663 7.33301 14.3679 7.33301 13.9997V8.45944H1.99967C1.63148 8.45944 1.33301 8.16097 1.33301 7.79278C1.33301 7.42459 1.63148 7.12611 1.99967 7.12611H7.33301V1.99967C7.33301 1.63148 7.63148 1.33301 7.99967 1.33301Z"
                                        fill="#19191A"
                                    />
                                </svg>
                            }
                        >
                            {getTitle('1211', 'Add reference')}
                        </ApplyV2Button>
                    </div>
                )}
            </div>
            <div className={`ApplyDialogV2__next ${baseClass}__next`}>
                {!settingsData?.steps?.reference?.required && (
                    <ApplyV2Button
                        variant={'transparent'}
                        className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-skip`}
                        rightIcon={'>'}
                        // isDisabled={!isValid || isLoading}
                        onClick={() => {
                            onNextStep()
                        }}
                    >
                        {getTitle('6107', 'Skip')}
                    </ApplyV2Button>
                )}
                <ApplyV2Button
                    variant={'primary'}
                    className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-submit ${baseClass}__next--btn`}
                    onClick={onNextStep}
                    isLoading={isPostApplied || isPostProfileLoading}
                    isDisabled={isPostApplied || isPostProfileLoading || (!isNoItems && items.length === 0)}
                    type={'submit'}
                >
                    {getTitle('3035', 'Next')}
                </ApplyV2Button>
            </div>
        </div>
    )
}

export default ApplyV2ReferenceStep2
